import { SearchResponse, SearchOptions } from "@algolia/client-search"
import { SearchIndex } from "algoliasearch/lite"

import { CoordinatesType, PractitionerSearchType } from "../../types/SearchPageTypes"

export interface PerformSearchParams {
  searchIndex: SearchIndex
  selectedTherapies: string[]
  selectedHealthIssues: string[]
  selectedLocations: string[]
  offersFreeConsult: boolean
  selectedLanguages: string[]
  selectedAges: string[]
  currentPage: number
  boundingBox?: number[]
  showMap: boolean
  radiusInMiles: number | null
  mapCenter: CoordinatesType | null
  isTablet?: boolean
  query: string
  similarQuery?: boolean
  additionalFilters?: string
  virtualFallback?: boolean
  hitsPerPage?: number
}

interface GetSearchOptionsParams
  extends Pick<
    PerformSearchParams,
    "boundingBox" | "radiusInMiles" | "mapCenter" | "isTablet" | "selectedLocations" | "currentPage"
  > {
  filters: string
}

// Used to leverage @algolia/client-search's SearchOptions type
export type WritableSearchOptions = {
  -readonly [P in keyof SearchOptions]: SearchOptions[P]
}

type FiltersStringParams = Pick<
  PerformSearchParams,
  | "selectedTherapies"
  | "selectedHealthIssues"
  | "selectedLocations"
  | "offersFreeConsult"
  | "selectedLanguages"
  | "selectedAges"
>

type ShouldShowMapParams = Pick<PerformSearchParams, "isTablet" | "mapCenter" | "selectedLocations">

type IsVirtualViewParams = Pick<PerformSearchParams, "selectedLocations">

interface UpdateUrlParams
  extends Pick<
    PerformSearchParams,
    | "query"
    | "radiusInMiles"
    | "mapCenter"
    | "selectedTherapies"
    | "selectedHealthIssues"
    | "selectedLocations"
    | "offersFreeConsult"
    | "selectedLanguages"
    | "selectedAges"
    | "boundingBox"
  > {
  mapZoom: number
  isMobile?: boolean
  poppingState: boolean
  setPoppingState: (poppingState: boolean) => void
}

const checkFilterInclusion = (targetArray: string[], mappedResult: string) =>
  targetArray.length > 0 ? `(${mappedResult})` : null

function filtersString({ ...filtersStringParams }: Readonly<FiltersStringParams>): string {
  const {
    selectedTherapies,
    selectedHealthIssues,
    selectedLocations,
    offersFreeConsult,
    selectedLanguages,
    selectedAges
  } = filtersStringParams

  const selectedTherapyMap = selectedTherapies
    ?.map((therapy) => `therapies:"${therapy.replace(/\s*\(.*\)$/, "")}"`)
    ?.join(" OR ")

  const selectedTherapyFilters = checkFilterInclusion(selectedTherapies, selectedTherapyMap)

  const selectedHealthIssueMap = selectedHealthIssues?.map((condition) => `health_issues:"${condition}"`)?.join(" AND ")

  const selectedHealthIssueFilters = checkFilterInclusion(selectedHealthIssues, selectedHealthIssueMap)

  const selectedLocationMap = selectedLocations?.map((location) => `location_types:"${location}"`)?.join(" OR ")

  const selectedLocationFilters = checkFilterInclusion(selectedLocations, selectedLocationMap)

  const freeConsultFilter = offersFreeConsult ? "free_consult:true" : null

  const selectedLanguageMap = selectedLanguages?.map((language) => `languages:"${language}"`)?.join(" OR ")

  const selectedLanguageFilters = checkFilterInclusion(selectedLanguages, selectedLanguageMap)

  const selectedAgeWorkWithMap = selectedAges?.map((age) => `age_work_withs:"${age}"`)?.join(" OR ")

  const selectedAgeWorkWithFilters = checkFilterInclusion(selectedAges, selectedAgeWorkWithMap)

  return [
    selectedTherapyFilters,
    selectedHealthIssueFilters,
    selectedLocationFilters,
    freeConsultFilter,
    selectedLanguageFilters,
    selectedAgeWorkWithFilters
  ]
    .filter(Boolean)
    .join(" AND ")
}

function shouldShowMap({ ...shouldShowMapParams }: Readonly<ShouldShowMapParams>): boolean {
  const { isTablet, mapCenter, selectedLocations } = shouldShowMapParams

  return (
    (typeof window === "undefined" && !isTablet) ||
    (!!mapCenter &&
      !isTablet &&
      (selectedLocations.includes("home") || selectedLocations.includes("office") || selectedLocations.length === 0))
  )
}

function isVirtualView({ ...isVirtualViewParams }: Readonly<IsVirtualViewParams>): boolean {
  const { selectedLocations } = isVirtualViewParams

  return selectedLocations.includes("virtual") && selectedLocations.length === 1
}

function getSearchOptions({
  ...getSearchOptionsParams
}: Readonly<GetSearchOptionsParams>): WritableSearchOptions | null {
  const { filters, boundingBox, radiusInMiles, mapCenter, isTablet, selectedLocations, currentPage } =
    getSearchOptionsParams

  const options: WritableSearchOptions = {
    page: currentPage,
    filters,
    facets: ["health_issues", "languages", "age_work_withs"]
  }

  const virtualView = selectedLocations.includes("virtual") && selectedLocations.length === 1
  const showMap =
    (typeof window === "undefined" && !isTablet) ||
    (!isTablet &&
      (selectedLocations.includes("home") || selectedLocations.includes("office") || selectedLocations.length === 0))

  // Checking for `boundingBox.length === 4` ensures the same behavior without
  // crashing the app when initializing SearchPage
  if (showMap && boundingBox && boundingBox.length === 4) {
    options.insideBoundingBox = [boundingBox]
  } else if (isTablet && mapCenter) {
    if (radiusInMiles) {
      options.aroundRadius = radiusInMiles * 1609
      options.aroundPrecision = 5 * 1609
    }
    options.aroundLatLng = [mapCenter.lat, mapCenter.lng].join(",")
  } else if (virtualView && !isTablet) {
    // no early return or additional options for virtual web view
  } else {
    return null
  }

  return options
}

function performSearch(
  { ...searchParams }: PerformSearchParams,
  onSuccess: (searchResponse: SearchResponse<PractitionerSearchType>) => void
): void {
  const {
    searchIndex,
    selectedTherapies,
    selectedHealthIssues,
    selectedLocations,
    offersFreeConsult,
    selectedLanguages,
    selectedAges,
    currentPage,
    boundingBox,
    showMap, // Not being used
    radiusInMiles,
    mapCenter,
    isTablet,
    query,
    similarQuery,
    additionalFilters,
    virtualFallback,
    hitsPerPage = isTablet ? 3 : 25
  } = searchParams

  const filters = filtersString({
    selectedTherapies,
    selectedHealthIssues,
    selectedLocations,
    offersFreeConsult,
    selectedLanguages,
    selectedAges
  })

  const searchOptions = getSearchOptions({
    filters,
    boundingBox,
    radiusInMiles,
    mapCenter,
    isTablet,
    selectedLocations,
    currentPage
  })

  if (mapCenter === null || searchOptions === null) {
    return // don't trigger a search if the search options state isn't fully initialized
  }

  searchOptions.hitsPerPage = hitsPerPage

  if (similarQuery) {
    searchOptions.similarQuery = query
  }

  if (additionalFilters) {
    if (searchOptions.filters) {
      searchOptions.filters = `${filters} AND ${additionalFilters}`
    } else {
      searchOptions.filters = additionalFilters
    }
  }

  if (virtualFallback) {
    delete searchOptions.aroundLatLng
    delete searchOptions.aroundRadius
  }

  searchOptions.attributesToHighlight = []

  searchIndex.search<PractitionerSearchType>(query, searchOptions).then((searchResponse) => {
    onSuccess(searchResponse)
  })
}

function buildSearchString({ ...searchParams }: Readonly<PerformSearchParams>): string {
  const {
    selectedTherapies,
    selectedHealthIssues,
    selectedLocations,
    offersFreeConsult,
    selectedLanguages,
    selectedAges,
    mapCenter,
    radiusInMiles,
    boundingBox,
    query
  } = searchParams

  let searchString = ""

  if (selectedTherapies.length > 0) {
    searchString += selectedTherapies.join(",") + ";"
  }

  if (selectedHealthIssues.length > 0) {
    searchString += selectedHealthIssues.join(",") + ";"
  }

  if (selectedLocations.length > 0) {
    searchString += selectedLocations.join(",") + ";"
  }

  if (offersFreeConsult) {
    searchString += offersFreeConsult + ";"
  }

  if (selectedLanguages.length > 0) {
    searchString += selectedLanguages.join(",") + ";"
  }

  if (selectedAges.length > 0) {
    searchString += selectedAges.join(",") + ";"
  }

  if (mapCenter) {
    searchString += mapCenter.lat + ";" + mapCenter.lng + ";"
  }

  if (radiusInMiles) {
    searchString += radiusInMiles + ";"
  }

  if (boundingBox) {
    searchString += boundingBox + ";"
  }

  if (query) {
    searchString += query + ";"
  }

  return searchString
}

function updateUrl({ ...updateUrlParams }: Readonly<UpdateUrlParams>): void {
  const {
    query,
    radiusInMiles,
    mapCenter,
    mapZoom,
    selectedTherapies,
    selectedHealthIssues,
    selectedLocations,
    offersFreeConsult,
    selectedLanguages,
    selectedAges,
    boundingBox,
    isMobile,
    poppingState,
    setPoppingState
  } = updateUrlParams

  const queryParams = new URLSearchParams()
  if (query) queryParams.set("query", query)
  if (radiusInMiles && isMobile) queryParams.set("radius", radiusInMiles.toString())
  if (mapCenter) queryParams.set("mapCenter", [mapCenter.lat, mapCenter.lng].join(","))
  if (selectedTherapies.length) queryParams.set("therapies", selectedTherapies.join(","))
  if (selectedHealthIssues.length) queryParams.set("healthIssues", selectedHealthIssues.join(","))
  if (selectedLocations.length) queryParams.set("locations", selectedLocations.join(","))
  if (offersFreeConsult) queryParams.set("freeConsult", offersFreeConsult.toString())
  if (selectedLanguages.length) queryParams.set("languages", selectedLanguages.join(","))
  if (selectedAges.length) queryParams.set("ages", selectedAges.join(","))
  if (boundingBox) queryParams.set("boundingBox", boundingBox.join(","))
  if (mapZoom) queryParams.set("mapZoom", mapZoom.toString())

  const currentParams = new URLSearchParams(window.location.search)
  if (currentParams.has("debug")) queryParams.set("debug", "true")

  if (poppingState) {
    setPoppingState(false)
    return
  }

  window.history.pushState(
    { queryString: queryParams.toString() },
    "",
    `/practitioner-search?${queryParams.toString()}`
  )
}

function doubleBoundingBoxSize(mapCenter: CoordinatesType | null, boundingBox?: number[]): number[] | null {
  if (!mapCenter || !boundingBox) return null
  if (boundingBox.length !== 4) return null

  const deltaX = (boundingBox[2]! - boundingBox[0]!) / 2
  const deltaY = (boundingBox[1]! - boundingBox[3]!) / 2

  const newTopLeft = [mapCenter.lat - deltaX * 2, mapCenter.lng + deltaY * 2]
  const newBottomRight = [mapCenter.lat + deltaX * 2, mapCenter.lng - deltaY * 2]

  return newTopLeft.concat(newBottomRight)
}

/**
 * Resolve map center from practitioner-search URL params.
 * Prefer `mapCenter=lat,lng` (search page / Featured links). Fall back to legacy
 * homepage/header params `aroundLatLng[lat]` + `aroundLatLng[lng]` so mobile web
 * searches keep the location the user actually submitted.
 */
function parseMapCenterFromSearchParams(urlParams: URLSearchParams): CoordinatesType | null {
  const mapCenterParam = urlParams.get("mapCenter")
  if (mapCenterParam) {
    const [lat, lng] = mapCenterParam.split(",").map((coord) => parseFloat(coord))
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat: lat as number, lng: lng as number }
    }
  }

  const aroundLat = parseFloat(urlParams.get("aroundLatLng[lat]") ?? "")
  const aroundLng = parseFloat(urlParams.get("aroundLatLng[lng]") ?? "")
  if (Number.isFinite(aroundLat) && Number.isFinite(aroundLng)) {
    return { lat: aroundLat, lng: aroundLng }
  }

  return null
}

export {
  performSearch,
  buildSearchString,
  updateUrl,
  doubleBoundingBoxSize,
  shouldShowMap,
  isVirtualView,
  parseMapCenterFromSearchParams
}
